/**
 * Dates civiles — noyau Foyer
 *
 * Toutes les échéances d'un foyer sont des *jours calendaires*, pas des instants :
 * « ramonage le 8 septembre » n'a pas d'heure. On les représente donc par une
 * chaîne `AAAA-MM-JJ` et on calcule dessus sans jamais passer par `Date`.
 *
 * Pourquoi ce détour : `new Date('2026-03-29')` est interprété en UTC, affiché en
 * Europe/Paris, et les deux ne tombent pas le même jour autour des changements
 * d'heure. Toute arithmétique fondée sur `(t2 - t1) / 86400000` se décale d'un
 * jour deux fois par an (la nuit du dernier dimanche de mars ne fait que 23 h).
 * En restant en date civile pure — algorithme `days_from_civil` de Howard
 * Hinnant — le problème n'existe pas : mars et octobre se comportent comme
 * février.
 *
 * Le fuseau Europe/Paris n'intervient qu'à un seul endroit : décider quel jour
 * il est *maintenant* et à quelle heure locale on est, pour le planificateur de
 * notifications. Voir `maintenantParis()`.
 */
export const FUSEAU = 'Europe/Paris';
const RE_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const RE_HEURE = /^([01]\d|2[0-3]):([0-5]\d)$/;
export class ErreurDate extends Error {
}
export const MOIS_LONG = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];
export const MOIS_COURT = [
    'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
    'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];
/** Indexé sur `jourSemaine()` : 1 = lundi … 7 = dimanche. */
export const JOURS_LONG = [
    '', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche',
];
export function estBissextile(annee) {
    return (annee % 4 === 0 && annee % 100 !== 0) || annee % 400 === 0;
}
const JOURS_PAR_MOIS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
/** Nombre de jours du mois (`mois` de 1 à 12). */
export function joursDansMois(annee, mois) {
    if (mois < 1 || mois > 12)
        throw new ErreurDate(`mois hors bornes : ${mois}`);
    if (mois === 2 && estBissextile(annee))
        return 29;
    return JOURS_PAR_MOIS[mois - 1];
}
/** Vrai si `v` est une date civile *existante* (le 29/02/2023 est rejeté). */
export function estDateIso(v) {
    if (typeof v !== 'string')
        return false;
    const m = RE_ISO.exec(v);
    if (!m)
        return false;
    const annee = Number(m[1]);
    const mois = Number(m[2]);
    const jour = Number(m[3]);
    if (mois < 1 || mois > 12)
        return false;
    return jour >= 1 && jour <= joursDansMois(annee, mois);
}
export function estHeureIso(v) {
    return typeof v === 'string' && RE_HEURE.test(v);
}
/** Construit une date civile ; lève si elle n'existe pas au calendrier. */
export function dateIso(annee, mois, jour) {
    const d = `${String(annee).padStart(4, '0')}-${String(mois).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
    if (!estDateIso(d))
        throw new ErreurDate(`date inexistante : ${d}`);
    return d;
}
/** Comme `dateIso()` mais renvoie `null` au lieu de lever (jour absent du mois). */
export function dateIsoOuNull(annee, mois, jour) {
    if (mois < 1 || mois > 12)
        return null;
    if (jour < 1 || jour > joursDansMois(annee, mois))
        return null;
    return dateIso(annee, mois, jour);
}
export function parties(d) {
    const m = RE_ISO.exec(d);
    if (!m)
        throw new ErreurDate(`format attendu AAAA-MM-JJ, reçu « ${d} »`);
    const p = { annee: Number(m[1]), mois: Number(m[2]), jour: Number(m[3]) };
    if (!estDateIso(d))
        throw new ErreurDate(`date inexistante : ${d}`);
    return p;
}
/**
 * Numéro de jour depuis l'époque Unix (1970-01-01 = 0), négatif avant.
 * Algorithme `days_from_civil` — exact, entier, sans fuseau.
 */
export function numeroJour(d) {
    const { annee, mois, jour } = parties(d);
    const y = annee - (mois <= 2 ? 1 : 0);
    const era = Math.floor(y / 400);
    const yoe = y - era * 400; // 0..399
    const doy = Math.floor((153 * (mois + (mois > 2 ? -3 : 9)) + 2) / 5) + jour - 1; // 0..365
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy; // 0..146096
    return era * 146097 + doe - 719468;
}
/** Inverse de `numeroJour()` — algorithme `civil_from_days`. */
export function deNumeroJour(n) {
    const z = n + 719468;
    const era = Math.floor(z / 146097);
    const doe = z - era * 146097; // 0..146096
    const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
    const y = yoe + era * 400;
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100)); // 0..365
    const mp = Math.floor((5 * doy + 2) / 153); // 0..11
    const jour = doy - Math.floor((153 * mp + 2) / 5) + 1; // 1..31
    const mois = mp + (mp < 10 ? 3 : -9); // 1..12
    return dateIso(y + (mois <= 2 ? 1 : 0), mois, jour);
}
/** Nombre de jours de `a` à `b` (négatif si `b` précède `a`). Jamais faussé par l'heure d'été. */
export function joursEntre(a, b) {
    return numeroJour(b) - numeroJour(a);
}
export function ajouteJours(d, n) {
    return deNumeroJour(numeroJour(d) + n);
}
/** 1 = lundi … 7 = dimanche (numérotation ISO 8601). */
export function jourSemaine(d) {
    const z = numeroJour(d);
    return (((z + 3) % 7) + 7) % 7 + 1;
}
/** Lundi de la semaine contenant `d` (semaine ISO, commence le lundi). */
export function lundiDeLaSemaine(d) {
    return ajouteJours(d, 1 - jourSemaine(d));
}
export function dernierJourDuMois(annee, mois) {
    return dateIso(annee, mois, joursDansMois(annee, mois));
}
/**
 * Ajoute `n` mois en conservant le jour du mois.
 * Renvoie `null` si le jour n'existe pas dans le mois d'arrivée (31 janvier + 1 mois),
 * conformément à la RFC 5545 qui *saute* ces occurrences au lieu de les tronquer.
 * Pour la sémantique « dernier jour du mois », utiliser `BYMONTHDAY=-1`.
 */
export function ajouteMois(d, n) {
    const { annee, mois, jour } = parties(d);
    const total = annee * 12 + (mois - 1) + n;
    return dateIsoOuNull(Math.floor(total / 12), (((total % 12) + 12) % 12) + 1, jour);
}
/** Variante tronquée : 31 janvier + 1 mois → 28 (ou 29) février. Utile pour l'affichage, pas pour la RRULE. */
export function ajouteMoisTronque(d, n) {
    const { annee, mois, jour } = parties(d);
    const total = annee * 12 + (mois - 1) + n;
    const a = Math.floor(total / 12);
    const m = (((total % 12) + 12) % 12) + 1;
    return dateIso(a, m, Math.min(jour, joursDansMois(a, m)));
}
/** Ajoute `n` années ; renvoie `null` pour le 29 février d'une année non bissextile. */
export function ajouteAns(d, n) {
    const { annee, mois, jour } = parties(d);
    return dateIsoOuNull(annee + n, mois, jour);
}
export function minDate(a, b) {
    return a <= b ? a : b;
}
export function maxDate(a, b) {
    return a >= b ? a : b;
}
// ── Formatage français ──────────────────────────────────────────────────────
/** « 8 septembre 2026 » */
export function enClair(d) {
    const { annee, mois, jour } = parties(d);
    return `${jour === 1 ? '1er' : jour} ${MOIS_LONG[mois - 1]} ${annee}`;
}
/** « 8 sept. » */
export function enCourt(d) {
    const { mois, jour } = parties(d);
    return `${jour === 1 ? '1er' : jour} ${MOIS_COURT[mois - 1]}`;
}
/** « mardi 11 août » */
export function enClairAvecJour(d) {
    return `${JOURS_LONG[jourSemaine(d)]} ${enCourt(d)}`;
}
export function nomMois(mois) {
    return MOIS_LONG[mois - 1];
}
export function nomJourSemaine(js) {
    return JOURS_LONG[js];
}
// ── Le seul point de contact avec le fuseau horaire ─────────────────────────
const FORMATEUR_PARIS = new Intl.DateTimeFormat('fr-FR', {
    timeZone: FUSEAU,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});
/**
 * Jour et heure *à Paris*, quel que soit le fuseau du conteneur.
 * C'est la seule fonction du noyau qui dépende de l'horloge : tout le reste
 * prend la date du jour en paramètre, pour rester testable.
 */
export function maintenantParis(instant = new Date()) {
    const p = {};
    for (const morceau of FORMATEUR_PARIS.formatToParts(instant)) {
        if (morceau.type !== 'literal')
            p[morceau.type] = morceau.value;
    }
    // Certaines versions d'ICU rendent « 24 » pour minuit avec hour12:false.
    const heure = p.hour === '24' ? '00' : p.hour;
    return {
        date: `${p.year}-${p.month}-${p.day}`,
        heure: `${heure}:${p.minute}`,
    };
}
export function aujourdhuiParis(instant) {
    return maintenantParis(instant).date;
}
//# sourceMappingURL=dates.js.map